// ── Business hours of operation (Task #1331) ─────────────────────────────────
// A Google-style hours-of-operation model: seven days (Mon→Sun), each with an
// open/close time and a per-day "Closed" toggle, plus a single "Not Applicable"
// escape for businesses without fixed public hours (online-only, by-appointment,
// etc.). Collected as a required field in onboarding Basics and displayed on the
// public /p/<slug> footer. Stored on business_profile as a single jsonb column.

// One day's hours. `open`/`close` are 24-hour "HH:MM" strings (the native
// <input type="time"> value); empty when unset. `closed` marks the day closed
// (open/close are ignored when true).
export type BusinessHoursDay = {
  open: string;
  close: string;
  closed: boolean;
};

// The full week. `notApplicable` short-circuits everything (the business has no
// fixed public hours) — when true the `days` are ignored for display + gating.
export type BusinessHours = {
  notApplicable: boolean;
  days: BusinessHoursDay[];
};

// Display labels, Monday-first (index 0 = Monday … 6 = Sunday).
export const BUSINESS_HOURS_DAY_LABELS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

export const BUSINESS_HOURS_DAY_COUNT = BUSINESS_HOURS_DAY_LABELS.length;

export function makeEmptyBusinessHoursDay(): BusinessHoursDay {
  return { open: "", close: "", closed: false };
}

// Start without invented opening hours. The database default mirrors this.
export const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  notApplicable: false,
  days: BUSINESS_HOURS_DAY_LABELS.map(() => makeEmptyBusinessHoursDay()),
};

/** Complete owner hours, excluding ambiguous, unconfirmed legacy sample hours. */
export function hasConfiguredBusinessHours(raw: unknown, savedByOwner = false): boolean {
  if (!raw || typeof raw !== "object") return false;
  const candidate = raw as Partial<BusinessHours>;
  if (candidate.notApplicable !== false || !Array.isArray(candidate.days) || candidate.days.length !== 7) return false;
  const hours = normalizeBusinessHours(raw);
  const validTime = (value: string) => /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
  if (!hours.days.every((day) => day.closed ||
    (validTime(day.open) && validTime(day.close) && day.open !== day.close))) return false;
  const legacySample = hours.days.every((day) => !day.closed && day.open === "08:00" && day.close === "17:00");
  return !legacySample || savedByOwner;
}

// Coerce any stored/incoming value into a well-formed BusinessHours with exactly
// seven days. Tolerates null/partial rows (older accounts, hand-edited JSON).
export function normalizeBusinessHours(raw: unknown): BusinessHours {
  const obj = (raw && typeof raw === "object" ? raw : {}) as Partial<BusinessHours>;
  const rawDays = Array.isArray(obj.days) ? obj.days : [];
  const days: BusinessHoursDay[] = [];
  for (let i = 0; i < BUSINESS_HOURS_DAY_COUNT; i++) {
    const d = (rawDays[i] && typeof rawDays[i] === "object"
      ? rawDays[i]
      : {}) as Partial<BusinessHoursDay>;
    days.push({
      open: typeof d.open === "string" ? d.open : "",
      close: typeof d.close === "string" ? d.close : "",
      closed: d.closed === true,
    });
  }
  return { notApplicable: obj.notApplicable === true, days };
}

// Onboarding completeness gate: the field is satisfied when the owner marked it
// Not Applicable, OR every day is resolved (either explicitly Closed, or has
// BOTH an open and a close time).
export function isBusinessHoursComplete(raw: unknown): boolean {
  const h = normalizeBusinessHours(raw);
  if (h.notApplicable) return true;
  return h.days.every((d) => d.closed || (!!d.open && !!d.close));
}

// True when there is at least one open day worth rendering publicly.
export function hasDisplayableBusinessHours(raw: unknown): boolean {
  const h = normalizeBusinessHours(raw);
  if (h.notApplicable) return false;
  return h.days.some((d) => !d.closed && !!d.open && !!d.close);
}

// Format a 24-hour "HH:MM" string into a friendly "9:00 AM" label. Returns the
// input unchanged when it isn't parseable.
export function formatBusinessHoursTime(value: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec((value || "").trim());
  if (!m) return value || "";
  let hour = Number(m[1]);
  const minute = m[2];
  if (Number.isNaN(hour) || hour < 0 || hour > 23) return value;
  const period = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minute} ${period}`;
}

// A single day's public display string: "Closed" or "9:00 AM – 5:00 PM".
export function formatBusinessHoursDay(day: BusinessHoursDay): string {
  if (day.closed || !day.open || !day.close) return "Closed";
  return `${formatBusinessHoursTime(day.open)} – ${formatBusinessHoursTime(day.close)}`;
}
